import { ReactNode } from 'react'

import { TableColumnsType, Typography } from 'antd'
import { isFunction, isNil, isString } from 'lodash'

import { NavigateButton } from '@eastcoal/common/components/Button/NavigateButton'

import { AliCopyIcon } from '@eastcoal/icon/AliCopyIcon'

import { GenericalRecord } from '../../../types/utilities'
import { ProField } from '../../ProField'
import { FilterSearch } from '../components/FilterSearch'
import { LinkType, ProColumnType, ProColumnUnionType } from '../interface'

import { resolveLink } from './resolveLink'

const enhanceText = (
  dom: ReactNode,
  value: any,
  record: GenericalRecord,
  { copyable, ellipsis }: ProColumnType<GenericalRecord, GenericalRecord>,
  context: GenericalRecord
) => {
  return copyable || ellipsis ? (
    <Typography.Text
      ellipsis={ellipsis ? { tooltip: value } : undefined}
      copyable={
        (isFunction(copyable) ? copyable(value, record, context) : copyable)
          ? {
              text: value,
              icon: <AliCopyIcon key='1' />
            }
          : undefined
      }
    >
      {dom}
    </Typography.Text>
  ) : (
    dom
  )
}

const renderText = (
  value: string,
  column: ProColumnType<GenericalRecord, GenericalRecord>,
  cellEmptyText: string
) => {
  const { valueType, valueEnum } = column

  if (valueEnum) {
    const valMap = valueEnum[value]
    return valMap ? (
      <ProField
        color={valMap.color}
        emptyText={cellEmptyText}
        tag={valMap.tag}
        type='status'
        value={valMap.text}
      />
    ) : (
      value
    )
  }

  if (isNil(valueType)) {
    return value
  }

  const proFieldProps = isString(valueType)
    ? {
        type: valueType
      }
    : valueType

  return <ProField {...proFieldProps} emptyText={cellEmptyText} value={value} />
}

const renderLink = (
  dom: ReactNode,
  value: unknown,
  record: GenericalRecord,
  index: number,
  link?: LinkType<GenericalRecord>
) => {
  if (isNil(link)) {
    return dom
  }

  const props = resolveLink(link, value, record, index)

  return (
    <NavigateButton
      plain
      underline
      forbiddenStrategy='disable'
      type='link'
      {...props}
    >
      {dom}
    </NavigateButton>
  )
}

const replaceRender = (
  column: ProColumnType<GenericalRecord, GenericalRecord>,
  columnEmptyText: string,
  context: GenericalRecord
): ProColumnType<GenericalRecord, GenericalRecord> => {
  const { link, render } = column

  return {
    ...column,
    render(value, record, index) {
      // 操作列或者外部自定义了 render 则不做处理
      if (render) {
        return render?.(value, record, index, context)
      }

      const textDom = renderText(
        isNil(value) ? '' : `${value}`,
        column,
        columnEmptyText
      )

      const linkDom = renderLink(textDom, value, record, index, link)
      const enhancedDom = enhanceText(linkDom, value, record, column, context)

      return enhancedDom
    }
  }
}

const enhanceFilter = (
  column: ProColumnType<GenericalRecord, GenericalRecord>
): ProColumnType<GenericalRecord, GenericalRecord> => {
  const { title, filterMode } = column

  if (filterMode === 'search') {
    return {
      ...column,
      filterDropdown: props => (
        <FilterSearch
          placeholder={
            typeof title === 'string' ? `请输入${title}...` : undefined
          }
          {...props}
        />
      )
    }
  }

  return column
}

const enhanceColumns = (
  columns: ProColumnUnionType<GenericalRecord, GenericalRecord>[],
  cellEmptyText: string,
  context: GenericalRecord
): ProColumnUnionType<GenericalRecord, GenericalRecord>[] => {
  return columns.map(item => {
    const replacedColumn = replaceRender(item, cellEmptyText, context)
    const enhancedColumn = enhanceFilter(replacedColumn)
    return enhancedColumn
  })
}

const filterColumns = (
  columns: ProColumnUnionType<GenericalRecord, GenericalRecord>[],
  context: GenericalRecord
) => {
  return columns.reduce(
    (acc, { visible, ...rest }) => {
      if (visible?.(context) !== false) {
        if ('children' in rest) {
          rest.children = filterColumns(rest.children, context)
        }
        acc.push(rest)
      }
      return acc
    },
    [] as ProColumnUnionType<GenericalRecord, GenericalRecord>[]
  )
}

export const transformColumns = (
  columns: ProColumnUnionType<GenericalRecord, GenericalRecord>[],
  cellEmptyText: string,
  context: GenericalRecord
): TableColumnsType<GenericalRecord> => {
  const filteredColumns = filterColumns(columns, context)
  const enhancedColumns = enhanceColumns(
    filteredColumns,
    cellEmptyText,
    context
  )

  return enhancedColumns.map(item => {
    const { filterMode, render, ...restProperties } = item
    const antdFilterMode = filterMode === 'search' ? undefined : filterMode

    if ('children' in item) {
      const children = transformColumns(item.children, cellEmptyText, context)
      return {
        ...restProperties,
        children,
        render: render as any,
        filterMode: antdFilterMode
      }
    }

    return {
      ...restProperties,
      render: render as any,
      filterMode: antdFilterMode
    }
  })
}
